<template>
  <div id="solve-info-css">
    <BaseBread class="custom-bread">
      <BaseBreadItem to="/">首页</BaseBreadItem>
      <BaseBreadItem to="/productadv">解决方案</BaseBreadItem>
      <BaseBreadItem>{{ dataSource.articleTitle }}</BaseBreadItem>
    </BaseBread>
    <div class="article-container">
      <div class="article-wrapper">
        <h1 class="article-title">{{ dataSource.articleTitle }}</h1>
        <div class="article-meta">
          <span v-if="time" class="article-date">发布时间：{{ time }}</span>
        </div>
        <div class="edit_container article-content" v-dompurify-html="dataSource.articleContent"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { findArticleDetailById } from "/composables/api/home";
import { useStore } from 'vuex';

// 获取 store 以便检查当前设备类型
const store = useStore();
const isMobile = computed(() => store.getters['windowScreen/isMobile']);

// 使用 useRoute 获取路由参数
const route = useRoute();
const dataSource = ref({});
const time = ref();

// 获取路由参数中的 link
const link = route.params.link;

// 获取文章详情
onMounted(async () => {
  try {
    const data = await findArticleDetailById(link);
    dataSource.value = data.data;
    time.value = data.time;
    
    // 在内容加载完成后处理视频响应式
    nextTick(() => {
      makeVideosResponsive();
    });
  } catch (error) {
    console.error('获取文章详情失败:', error);
  }
});

// 处理视频响应式的函数
const makeVideosResponsive = () => {
  // 获取文章内容中的所有视频和iframe元素
  const articleContent = document.querySelector('.article-content');
  if (!articleContent) return;
  
  // 处理所有iframe（通常是嵌入视频）
  const iframes = articleContent.querySelectorAll('iframe');
  iframes.forEach(iframe => {
    // 检查iframe是否已经在响应式容器中
    if (!iframe.parentElement.classList.contains('video-wrapper')) {
      // 创建响应式容器并包裹iframe
      const wrapper = document.createElement('div');
      wrapper.className = 'video-wrapper';
      iframe.parentNode.insertBefore(wrapper, iframe);
      wrapper.appendChild(iframe);
    }
  });
  
  // 处理所有video元素
  const videos = articleContent.querySelectorAll('video');
  videos.forEach(video => {
    // 添加必要的属性
    video.setAttribute('controls', 'true');
    video.setAttribute('playsinline', 'true');
    video.style.maxWidth = '100%';
  });
};

// 当路由参数变化时重新加载
watch(() => route.params.link, async (newLink) => {
  if (newLink) {
    try {
      const data = await findArticleDetailById(newLink);
      dataSource.value = data.data;
      time.value = data.time;
      
      // 在内容变化后再次处理视频
      nextTick(() => {
        makeVideosResponsive();
      });
    } catch (error) {
      console.error('获取文章详情失败:', error);
    }
  }
});
</script>

<style scoped lang="less">
#solve-info-css {
  width: 100%;
  background-color: #fff;
  
  .custom-bread {
    margin: 0 5vh;
    
    @media (max-width: 767px) {
      margin: 0 15px;
      padding: 10px 0;
    }
  }
  
  .article-container {
    width: 100%;
    padding: 0 30px 50px;
    
    @media (max-width: 767px) {
      padding: 0 15px 30px;
    }
  }
  
  .article-wrapper {
    width: 80%;
    max-width: 1200px;
    margin: 0 auto;
    
    @media (max-width: 1024px) {
      width: 90%;
    }
    
    @media (max-width: 767px) {
      width: 100%;
    }
  }
  
  .article-title {
    text-align: center;
    font-size: 30px;
    margin-bottom: 20px;
    
    @media (max-width: 767px) {
      font-size: 22px;
      margin-bottom: 15px;
    }
  }
  
  .article-meta {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
    
    @media (max-width: 767px) {
      margin-bottom: 15px;
    }
    
    .article-date {
      color: #888;
      font-size: 14px;
    }
  }
  
  .article-content {
    text-align: left;
    line-height: 1.8;
    
    // 图片响应式处理
    :deep(img) {
      max-width: 100%;
      height: auto;
      margin: 10px 0;
    }
    
    // 视频响应式处理
    :deep(video),
    :deep(iframe),
    :deep(.video-container),
    :deep(.video-js),
    :deep(.html5-video-player),
    :deep(.video-player) {
      width: 100% !important;
      max-width: 100% !important;
      height: auto !important;
      min-height: 200px;
      aspect-ratio: 16/9;
      margin: 15px 0;
    }
    
    // 针对常见视频播放器的容器
    :deep(.player-container),
    :deep(.video-wrapper),
    :deep(.embed-responsive) {
      position: relative;
      width: 100% !important;
      max-width: 100% !important;
      padding-bottom: 56.25% !important; // 16:9 比例
      height: 0 !important;
      overflow: hidden;
      margin: 15px 0;
      
      iframe, 
      video,
      object,
      embed {
        position: absolute;
        top: 0;
        left: 0;
        width: 100% !important;
        height: 100% !important;
      }
    }
    
    // 表格响应式处理
    :deep(table) {
      width: 100%;
      border-collapse: collapse;
      overflow-x: auto;
      display: block;
      
      @media (max-width: 767px) {
        font-size: 14px;
      }
    }
    
    // 标题响应式
    :deep(h1, h2, h3, h4, h5, h6) {
      @media (max-width: 767px) {
        font-size: 90%;
      }
    }
    
    // 段落响应式
    :deep(p) {
      margin: 10px 0;
      
      @media (max-width: 767px) {
        font-size: 15px;
      }
    }
  }
}

// 覆盖全局样式，确保在移动设备上正确显示
.edit_container {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>
